import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import type { SearchDiagnostics, SearchDiagnosticsDelivery, SearchDiagnosticsLink, SearchDiagnosticsSource } from '@/api/types/searchDiagnostics';
import { AdminPageSections, CollapsiblePanel } from '@/components/common';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import { EmptyState, ErrorBanner, FilterSelect, MetricChip, SourceChips, StatusBadge, Toolbar } from '@/components/ui';
import { formatFiltersSummary } from '@/utils/searchFilters';
import { shortId } from '@/utils/jobStatus';

type Props = {
  searchId: string;
};

const PIPELINE_STEPS = [
  'Search',
  'Sources',
  'Latest scrape jobs',
  'Raw links',
  'Relevance filter',
  'Category filters',
  'New links',
  'Subscription links',
  'Delivery jobs',
  'Delivery result',
];

function sourceStatusVariant(row: SearchDiagnosticsSource): 'success' | 'danger' | 'warning' | 'neutral' | 'info' {
  if (row.skip_reason) return 'warning';
  if (!row.source_exists) return 'danger';
  if (!row.source_enabled) return 'warning';
  if (row.last_error || row.execution_status === 'failed') return 'danger';
  if (row.sent_count > 0) return 'success';
  if (row.execution_status === 'skipped') return 'warning';
  return 'neutral';
}

function sourceStatusLabel(row: SearchDiagnosticsSource): string {
  if (!row.source_exists) return 'Source missing';
  if (!row.source_enabled) return 'Source disabled';
  if (row.skip_reason) return `Skipped: ${row.skip_reason}`;
  if (row.last_error) return row.last_error;
  return row.execution_status || 'No runs yet';
}

function deliveryVariant(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (status === 'SENT') return 'success';
  if (status === 'FAILED') return 'danger';
  if (status === 'SKIPPED') return 'warning';
  return 'neutral';
}

export default function SearchDebugPanel({ searchId }: Props) {
  const [data, setData] = useState<SearchDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [linkSourceFilter, setLinkSourceFilter] = useState('');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('');
  const [deliverySourceFilter, setDeliverySourceFilter] = useState('');
  const [expandedSource, setExpandedSource] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.searches.diagnostics(searchId));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load diagnostics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sourceNames = useMemo(
    () => (data?.search?.source_names ?? []).slice().sort(),
    [data?.search?.source_names]
  );

  const filteredSources = useMemo(() => {
    const rows = data?.sources ?? [];
    if (!sourceFilter) return rows;
    return rows.filter((row) => row.source === sourceFilter);
  }, [data?.sources, sourceFilter]);

  const filteredLinks = useMemo(() => {
    let rows = data?.recent_links ?? [];
    if (linkSourceFilter) rows = rows.filter((row) => row.source_name === linkSourceFilter);
    return rows;
  }, [data?.recent_links, linkSourceFilter]);

  const filteredDeliveries = useMemo(() => {
    let rows = data?.recent_deliveries ?? [];
    if (deliveryStatusFilter) {
      rows = rows.filter((row) => row.status === deliveryStatusFilter);
    }
    if (deliverySourceFilter) {
      rows = rows.filter((row) => row.source_name === deliverySourceFilter);
    }
    return rows;
  }, [data?.recent_deliveries, deliveryStatusFilter, deliverySourceFilter]);

  const sourceColumns = useMemo<DataTableColumn<SearchDiagnosticsSource>[]>(
    () => [
      {
        id: 'source',
        header: 'Source',
        width: 140,
        render: (row) => (
          <>
            <SourceChips sources={[row.source]} variant="compact" />
            {!row.source_exists ? (
              <StatusBadge variant="danger">missing</StatusBadge>
            ) : !row.source_enabled ? (
              <StatusBadge variant="warning">disabled</StatusBadge>
            ) : null}
          </>
        ),
      },
      {
        id: 'job',
        header: 'Last job',
        width: 120,
        render: (row) =>
          row.latest_job_id ? (
            <Link to={`/jobs?job_id=${row.latest_job_id}`} className="table-link">
              {shortId(row.latest_job_id)}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'job_status',
        header: 'Job status',
        width: 100,
        render: (row) => row.latest_job_status || '—',
      },
      {
        id: 'raw',
        header: 'Raw',
        width: 60,
        render: (row) => row.raw_links_count,
      },
      {
        id: 'relevance',
        header: 'Relevance',
        width: 80,
        render: (row) => row.relevance_filtered_count,
      },
      {
        id: 'category',
        header: 'Filtered',
        width: 80,
        render: (row) => row.category_filtered_count ?? row.vehicle_filtered_count,
      },
      {
        id: 'new',
        header: 'New',
        width: 60,
        render: (row) => row.new_links_count,
      },
      {
        id: 'saved',
        header: 'Saved',
        width: 70,
        render: (row) => row.saved_links_count,
      },
      {
        id: 'queued',
        header: 'Queued',
        width: 70,
        render: (row) => row.delivery_queued_count,
      },
      {
        id: 'sent',
        header: 'Sent',
        width: 60,
        render: (row) => row.sent_count,
      },
      {
        id: 'failed',
        header: 'Failed',
        width: 70,
        render: (row) => row.failed_count,
      },
      {
        id: 'status',
        header: 'Status / error',
        width: 220,
        render: (row) => (
          <>
            <StatusBadge variant={sourceStatusVariant(row)}>{sourceStatusLabel(row)}</StatusBadge>
            {row.last_processed_at ? (
              <div className="muted">{new Date(row.last_processed_at).toLocaleString()}</div>
            ) : null}
          </>
        ),
      },
      {
        id: 'details',
        header: '',
        width: 90,
        render: (row) => (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setExpandedSource((current) => (current === row.source ? null : row.source))}
          >
            {expandedSource === row.source ? 'Hide' : 'Details'}
          </button>
        ),
      },
    ],
    [expandedSource]
  );

  const linkColumns = useMemo<DataTableColumn<SearchDiagnosticsLink>[]>(
    () => [
      {
        id: 'time',
        header: 'Found',
        width: 140,
        render: (row) => new Date(row.discovered_at).toLocaleString(),
      },
      {
        id: 'source',
        header: 'Source',
        width: 120,
        render: (row) =>
          row.source_name ? <SourceChips sources={[row.source_name]} variant="compact" /> : <span className="muted">—</span>,
      },
      {
        id: 'title',
        header: 'Title',
        width: 220,
        render: (row) => <TruncatedCell value={row.title || row.url} />,
      },
      {
        id: 'recipient',
        header: 'Recipient',
        width: 120,
        render: (row) => <TruncatedCell value={row.recipient_display_name} />,
      },
      {
        id: 'delivery',
        header: 'Delivery',
        width: 90,
        render: (row) =>
          row.delivery_status ? (
            <StatusBadge variant={deliveryVariant(row.delivery_status)}>{row.delivery_status}</StatusBadge>
          ) : (
            '—'
          ),
      },
      {
        id: 'url',
        header: 'URL',
        width: 240,
        render: (row) => <TruncatedCell value={row.url} href={row.url} copyable />,
      },
    ],
    []
  );

  const deliveryColumns = useMemo<DataTableColumn<SearchDiagnosticsDelivery>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        width: 140,
        render: (row) => (row.sent_at ? new Date(row.sent_at).toLocaleString() : '—'),
      },
      {
        id: 'status',
        header: 'Status',
        width: 90,
        render: (row) => <StatusBadge variant={deliveryVariant(row.status)}>{row.status}</StatusBadge>,
      },
      {
        id: 'source',
        header: 'Source',
        width: 120,
        render: (row) =>
          row.source_name ? <SourceChips sources={[row.source_name]} variant="compact" /> : <span className="muted">—</span>,
      },
      {
        id: 'recipient',
        header: 'Recipient',
        width: 120,
        render: (row) => <TruncatedCell value={row.recipient_display_name} />,
      },
      {
        id: 'error',
        header: 'Error',
        width: 180,
        render: (row) => <TruncatedCell value={row.error_message} />,
      },
      {
        id: 'url',
        header: 'URL',
        width: 240,
        render: (row) => <TruncatedCell value={row.url} href={row.url || undefined} copyable />,
      },
    ],
    []
  );

  if (loading && !data) {
    return (
      <div className="loading-inline" role="status">
        <span className="spinner" aria-hidden />
        Loading search diagnostics…
      </div>
    );
  }

  const summary = data?.summary;
  const search = data?.search;

  return (
    <div className="search-debug-panel">
      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      <CollapsiblePanel
        id="pipeline-flow"
        storageScope={`search-debug-${searchId}`}
        title="Pipeline"
        subtitle="End-to-end path from search to delivery"
        defaultExpanded
      >
        <p className="search-debug-pipeline-flow">{PIPELINE_STEPS.join(' → ')}</p>
      </CollapsiblePanel>

      <CollapsiblePanel
        id="summary-cards"
        storageScope={`search-debug-${searchId}`}
        title="Health summary"
        defaultExpanded
      >
        <div className="metric-chips">
          <MetricChip
            label="Last run"
            value={summary?.last_run_at ? new Date(summary.last_run_at).toLocaleString() : '—'}
          />
          <MetricChip label="Next run" value={summary?.next_run_at ? new Date(summary.next_run_at).toLocaleString() : '—'} />
          <MetricChip label="Active subscriptions" value={summary?.active_subscriptions ?? 0} variant="info" />
          <MetricChip label="Links found" value={summary?.links_found ?? 0} />
          <MetricChip label="Sent" value={summary?.sent_count ?? 0} variant="success" />
          <MetricChip label="Failed" value={summary?.failed_count ?? 0} variant="danger" />
          <MetricChip label="Skipped" value={summary?.skipped_count ?? 0} variant="warning" />
        </div>
      </CollapsiblePanel>

      <AdminPageSections>
        <CollapsiblePanel
          id="search-config"
          storageScope={`search-debug-${searchId}`}
          title="Search config"
          defaultExpanded={false}
        >
          {search ? (
            <dl className="search-summary-bar">
              <div className="search-summary-item">
                <dt>Enabled</dt>
                <dd>{search.is_enabled ? 'Yes' : 'No'}</dd>
              </div>
              <div className="search-summary-item">
                <dt>Category</dt>
                <dd>{search.category}</dd>
              </div>
              <div className="search-summary-item">
                <dt>Filters</dt>
                <dd>{formatFiltersSummary(search.filters_json, search.name)}</dd>
              </div>
              <div className="search-summary-item">
                <dt>Sources</dt>
                <dd>
                  <SourceChips sources={search.source_names ?? []} variant="compact" />
                </dd>
              </div>
            </dl>
          ) : null}
        </CollapsiblePanel>

        <CollapsiblePanel
          id="subscriptions"
          storageScope={`search-debug-${searchId}`}
          title="Subscriptions"
          badge={data?.subscriptions.length ?? 0}
          defaultExpanded
        >
          {!data?.subscriptions.length ? (
            <EmptyState title="No subscriptions" description="Add a subscription to deliver listings to recipients." />
          ) : (
            <div className="data-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Subscription</th>
                    <th>Recipient status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscriptions.map((sub) => (
                    <tr key={sub.id}>
                      <td>{sub.recipient_display_name || '—'}</td>
                      <td>
                        <StatusBadge variant={sub.is_enabled ? 'success' : 'neutral'}>
                          {sub.is_enabled ? 'enabled' : 'disabled'}
                        </StatusBadge>
                      </td>
                      <td>
                        {!sub.recipient_enabled ? (
                          <StatusBadge variant="warning">recipient disabled</StatusBadge>
                        ) : (
                          <StatusBadge variant="success">active</StatusBadge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CollapsiblePanel>

        <CollapsiblePanel
          id="source-pipeline"
          storageScope={`search-debug-${searchId}`}
          title="Per-source pipeline"
          badge={filteredSources.length}
          defaultExpanded
        >
          <Toolbar>
            <FilterSelect label="Source" htmlFor="debug-source-filter">
              <select
                id="debug-source-filter"
                className="filter-select"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="">All sources</option>
                {sourceNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </FilterSelect>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => void load()} disabled={loading}>
              Refresh
            </button>
          </Toolbar>

          {!filteredSources.length ? (
            <EmptyState
              title={sourceNames.length ? 'No jobs yet' : 'No sources configured'}
              description={
                sourceNames.length
                  ? 'Run a scrape from Searches to populate pipeline diagnostics.'
                  : 'Add sources to this search to begin collecting diagnostics.'
              }
            />
          ) : (
            <>
              <DataTable
                columns={sourceColumns}
                rows={filteredSources}
                getRowKey={(row) => row.source}
                columnStorageKey={`search-debug-sources-${searchId}`}
              />
              {expandedSource ? (
                <div className="search-debug-source-detail card card-elevated">
                  {filteredSources
                    .filter((row) => row.source === expandedSource)
                    .map((row) => (
                      <div key={row.source}>
                        <h4>{row.source} details</h4>
                        {row.filter_summary ? <p className="muted">{row.filter_summary}</p> : null}
                        {row.execution_id ? (
                          <p>
                            <Link to={`/pipeline?execution_id=${row.execution_id}`} className="table-link">
                              Open pipeline execution
                            </Link>
                            {row.latest_job_id ? (
                              <>
                                {' · '}
                                <Link to={`/jobs?job_id=${row.latest_job_id}`} className="table-link">
                                  Open job
                                </Link>
                              </>
                            ) : null}
                          </p>
                        ) : null}
                        <pre className="code-block">
                          {JSON.stringify(row.filter_diagnostics ?? {}, null, 2)}
                        </pre>
                      </div>
                    ))}
                </div>
              ) : null}
            </>
          )}
        </CollapsiblePanel>

        <CollapsiblePanel
          id="recent-links"
          storageScope={`search-debug-${searchId}`}
          title="Recent links"
          badge={filteredLinks.length}
          defaultExpanded={false}
        >
          <Toolbar>
            <FilterSelect label="Source" htmlFor="debug-link-source">
              <select
                id="debug-link-source"
                className="filter-select"
                value={linkSourceFilter}
                onChange={(e) => setLinkSourceFilter(e.target.value)}
              >
                <option value="">All sources</option>
                {sourceNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </FilterSelect>
          </Toolbar>
          <DataTable
            columns={linkColumns}
            rows={filteredLinks}
            getRowKey={(row) => `${row.subscription_id}-${row.link_id}`}
            emptyState={
              <EmptyState title="No links found" description="Links appear after a successful scrape for this search." />
            }
            columnStorageKey={`search-debug-links-${searchId}`}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          id="recent-deliveries"
          storageScope={`search-debug-${searchId}`}
          title="Recent deliveries"
          badge={filteredDeliveries.length}
          defaultExpanded={false}
        >
          <Toolbar>
            <FilterSelect label="Status" htmlFor="debug-delivery-status">
              <select
                id="debug-delivery-status"
                className="filter-select"
                value={deliveryStatusFilter}
                onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="SENT">Sent</option>
                <option value="FAILED">Failed</option>
                <option value="SKIPPED">Skipped</option>
              </select>
            </FilterSelect>
            <FilterSelect label="Source" htmlFor="debug-delivery-source">
              <select
                id="debug-delivery-source"
                className="filter-select"
                value={deliverySourceFilter}
                onChange={(e) => setDeliverySourceFilter(e.target.value)}
              >
                <option value="">All sources</option>
                {sourceNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </FilterSelect>
          </Toolbar>
          <DataTable
            columns={deliveryColumns}
            rows={filteredDeliveries}
            getRowKey={(row) => row.id}
            emptyState={
              <EmptyState title="No deliveries" description="Deliveries appear after the send worker notifies recipients." />
            }
            columnStorageKey={`search-debug-deliveries-${searchId}`}
          />
        </CollapsiblePanel>
      </AdminPageSections>
    </div>
  );
}
